'use client';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CTACardSignUpProps } from '@/components/cta/types';
import { Headline } from '@/components/headline/Headline';
import SerializeDescription from '@/components/serialize-description/SerializeDescription';
import Icon from '@/components/icon/Icon';
import '../styles.css';
import { Input } from '@/components/input/Input';
import { Button } from '@/components/button/Button';
import {
  buildInitialValues,
  submitFormData,
} from '@/components/cta/utils/utilsSignUp';
import { useTranslations } from '@/lib/locale-context';

const CTACardSignup: React.FC<CTACardSignUpProps> = ({
  headline,
  description,
  icon,
  sectionBackgroundColor,
  sectionId,
}) => {
  const [firstStage, setFirstStage] = useState(true);
  const [fadeIn, setFadeIn] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslations();

  const useUtmSourceData = () => {
    const [utmData, setUtmData] = useState({ sourceKey: '', sourceUrl: '' });

    useEffect(() => {
      if (typeof window !== 'undefined') {
        const urlParams = new URLSearchParams(window.location.search);
        const sourceKey = urlParams.get('utm_sourcekey') || 'olma24_qu';
        const sourceUrl = window.location.href || 'https://smzh.ch/de/';
        setUtmData({ sourceKey, sourceUrl });
      }
    }, []);

    return utmData;
  };
  const utmData = useUtmSourceData();

  const onClickToggleStageValue = () => {
    setFadeIn(false);
    setTimeout(() => {
      setFirstStage(!firstStage);
      setFadeIn(true);
    }, 500);
  };

  const formik = useFormik({
    initialValues: buildInitialValues(
      [
        { key: 'utmSource', value: utmData?.sourceKey || '' },
        { key: 'sourceUrl', value: utmData?.sourceUrl || '' },
      ],
      {
        firstName: '',
        lastName: '',
        email: '',
        consent: false,
      },
    ),
    validationSchema: Yup.object({
      firstName: Yup.string().required(t('newsletterForm.errors.firstName')),
      lastName: Yup.string().required(t('newsletterForm.errors.lastName')),
      email: Yup.string()
        .email(t('newsletterForm.errors.invalidEmail'))
        .required(t('newsletterForm.errors.email')),
      consent: Yup.boolean().oneOf([true], t('newsletterForm.errors.consent')),
    }),
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        const isSuccess = await submitFormData(values);
        if (isSuccess) {
          onClickToggleStageValue();
        }
      } catch (error) {
        console.error('Submission failed:', error);
        alert('An error occurred while submitting the form. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div
      id={sectionId}
      className={`relative w-full bg-${sectionBackgroundColor}`}
    >
      <form
        onSubmit={formik.handleSubmit}
        className={clsx(
          `cta-card-signup-container container mx-auto grid grid-cols-4 gap-4 rounded-xl px-4 text-white sm:px-0 lg:grid-cols-4 lg:px-0 xl:grid-cols-4`,
          {
            'bg-transparent': firstStage,
            'bg-white': !firstStage,
          },
        )}
      >
        <div
          className={clsx(
            'cta-card__signup col-start-1 col-end-5 flex flex-col align-middle',
            'relative col-span-full rounded-lg border-stone-700 bg-transparent',
          )}
        >
          <div
            className={clsx(
              {
                'fade-enter': fadeIn,
                'fade-exit': !fadeIn,
                'fade-enter-active': fadeIn,
                'fade-exit-active': !fadeIn,
              },
              'flex min-h-[460px] flex-col justify-end',
            )}
          >
            {!firstStage && (
              <div className="icon-container mx-auto w-fit rounded-3xl bg-brand-200 p-6">
                {firstStage ? (
                  <Icon {...icon} />
                ) : (
                  <Icon name={'mail-check'} size={'48px'} color={'#07314C'} />
                )}
              </div>
            )}
            {firstStage && (
              <Headline
                {...headline}
                className={'mr-left w-1/2 text-left font-bold'}
              >
                {headline?.title}
              </Headline>
            )}
            {firstStage ? (
              <div className="newsletter-description">
                {description && (
                  <SerializeDescription description={description} />
                )}
              </div>
            ) : (
              <div>
                <div className="mb-12 p-4 text-center text-[24px] font-medium text-brand-700">
                  {t('newsletterForm.thankYouMessage')}
                </div>
                <div className="mx-auto p-4 text-center">
                  <Button
                    aria-label={'Close'}
                    title={t('newsletterForm.close')}
                    name={t('newsletterForm.close')}
                    variant={'secondary'}
                    onClick={onClickToggleStageValue}
                    className={
                      'w-full justify-center border-brand-400 text-center text-[20px]'
                    }
                  />
                </div>
              </div>
            )}
            {firstStage && (
              <div className={'form-container mt-4 flex flex-col gap-4'}>
                <Input
                  value={formik.values.email}
                  variant={'dark'}
                  type={'email'}
                  name={'email'}
                  label={t('newsletterForm.emailLabel')}
                  error={!!formik.errors.email}
                  onChange={(value) => formik.setFieldValue('email', value)}
                />
                <Input
                  value={formik.values.firstName}
                  variant={'dark'}
                  type={'text'}
                  label={t('newsletterForm.firstNameLabel')}
                  name={'firstName'}
                  error={!!formik.errors.firstName}
                  onChange={(value) => formik.setFieldValue('firstName', value)}
                />
                <Input
                  value={formik.values.lastName}
                  variant={'dark'}
                  type={'text'}
                  label={t('newsletterForm.lastNameLabel')}
                  name={'lastName'}
                  error={!!formik.errors.lastName}
                  onChange={(value) => formik.setFieldValue('lastName', value)}
                />
                <Input
                  checked={formik.values.consent}
                  value={'checked'}
                  variant={'dark'}
                  type={'checkbox'}
                  name={'consent'}
                  label={t('newsletterForm.consentLabel')}
                  error={!!formik.errors.consent}
                  onChange={() =>
                    formik.setFieldValue('consent', !formik.values.consent)
                  }
                  className={'justify-center'}
                />
                <Button
                  aria-label="Submit"
                  className={
                    'w-full justify-center border border-white bg-transparent'
                  }
                  variant={'primary'}
                  name={
                    isSubmitting
                      ? t('newsletterForm.sending')
                      : t('newsletterForm.subscribe')
                  }
                  type={'submit'}
                  disabled={isSubmitting}
                />
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CTACardSignup;
